import type { ColumnDef } from '@tanstack/react-table';

import {
    type GetEquipmentsQuery,
    type GetSuppliersQuery,
    type GetTotemsWithEquipmentsQuery,
    EquipmentState,
} from 'data/__generated__';
import { textColumn, multiSelectColumn } from 'components/DataTable';

import { TotemImage } from 'components/TotemImage';
import { DetailLink } from 'components/DetailsView';
import { StatusTag, EQUIPMENT_STATES_LABELS } from 'pages/Equipments/components/StatusTag';

export type EquipmentRow = GetEquipmentsQuery['adminEquipments'][number];
type SupplierRow = GetSuppliersQuery['suppliers'][number];
type TotemWithEquipment = GetTotemsWithEquipmentsQuery['totemsWithEquipments'][number];

export const getEquipmentsColumns = (
    suppliers: SupplierRow[],
    totemsWithEquipments: TotemWithEquipment[],
): ColumnDef<EquipmentRow, unknown>[] => [
    textColumn<EquipmentRow, { searchValue: string; state: EquipmentState[] }>({
        accessorKey: 'imageUrl',
        header: 'Photo',
        defaultVisible: true,
        cell: ({ row }) => <TotemImage src={row.original.imageUrl || ''} alt="Pas d'image" size={100} />,
    }),
    textColumn<EquipmentRow, { searchValue: string; state: EquipmentState[] }>({
        accessorKey: 'fullname',
        header: 'Nom de produit',
        defaultVisible: true,
    }),
    multiSelectColumn<EquipmentRow, { searchValue: string; state: EquipmentState[] }>({
        accessorKey: 'state',
        header: 'Statut',
        filterKey: 'state',
        defaultVisible: true,
        options: Object.values(EquipmentState).map((state) => ({
            value: state,
            label: EQUIPMENT_STATES_LABELS[state],
        })),
        renderOption: (option) => <StatusTag state={option.value as EquipmentState} />,
        cell: ({ row }) => <StatusTag state={row.original.state} />,
    }),
    textColumn<EquipmentRow, { searchValue: string; state: EquipmentState[] }>({
        accessorFn: (row) => {
            const supplier = suppliers.find((s) => s._id === row.suppliers?.[0]?._id);
            return supplier?.name || 'Pas de fournisseur';
        },
        id: 'supplierName',
        header: 'Fournisseur',
        defaultVisible: true,
        cell: ({ row }) => {
            const supplier = suppliers.find((s) => s._id === row.original.suppliers?.[0]?._id);
            return (
                <DetailLink
                    path="supplier"
                    name={supplier?.name}
                    value={row.original.suppliers?.[0]?._id ?? 'Pas de fournisseur'}
                />
            );
        },
    }),
    textColumn<EquipmentRow, { searchValue: string; state: EquipmentState[] }>({
        accessorFn: (row) => {
            const totemsWithEquipment = totemsWithEquipments.filter(({ equipments }) =>
                equipments.some(({ equipmentId }) => equipmentId === row._id),
            );
            return totemsWithEquipment.map((t) => `${t.organization.name} - Totem #${t.number}`).join(', ');
        },
        id: 'totemsList',
        header: "Totems ayant l'équipement",
        defaultVisible: true,
        cell: ({ row }) => {
            const totemsWithEquipment = totemsWithEquipments.filter(({ equipments }) =>
                equipments.some(({ equipmentId }) => equipmentId === row.original._id),
            );
            return (
                <>
                    {totemsWithEquipment.map((totem) => {
                        const totemEquipment = totem.equipments.find(
                            ({ equipmentId }) => equipmentId === row.original._id,
                        );
                        return (
                            <div key={totem._id}>
                                • {totem.organization.name} - Totem #{totem.number}. Quantité:{' '}
                                {totemEquipment?.quantity ?? -1}
                            </div>
                        );
                    })}
                </>
            );
        },
    }),
];
